import React, { useEffect, useState, useRef, Children, createElement } from 'react';
import { motion } from 'framer-motion';
import { ExternalLinkIcon, LinkedinIcon, BookOpenIcon, MicIcon, PenToolIcon, RssIcon, Loader2Icon } from 'lucide-react';
import { Card, CardBody } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
interface Insight {
  id: string;
  type: 'article' | 'talk' | 'framework';
  title: string;
  excerpt: string;
  tag: string;
  icon: React.ElementType;
}
const insights: Insight[] = [{
  id: '1',
  type: 'article',
  title: 'Design Governance at Scale: Why Most Enterprise Systems Fail',
  excerpt: "The failure mode of enterprise design systems is almost never technical. It's organizational. Without governance structures that align incentives, even the most sophisticated component libraries devolve into inconsistency within 18 months.",
  tag: 'Design Systems',
  icon: BookOpenIcon
}, {
  id: '2',
  type: 'framework',
  title: 'The Accessibility-First Imperative in Federal Digital Modernization',
  excerpt: "Section 508 compliance is not a constraint — it's a design forcing function. Organizations that treat accessibility as a baseline rather than an afterthought consistently produce better experiences for all users, not just those with disabilities.",
  tag: 'Accessibility',
  icon: PenToolIcon
}, {
  id: '3',
  type: 'talk',
  title: 'AI Integration in Enterprise Brand Systems: Governance Before Velocity',
  excerpt: "The organizations winning with AI are not the ones moving fastest. They're the ones that established governance frameworks first — defining what AI can and cannot do within their brand system before deploying it at scale.",
  tag: 'AI/GAI Strategy',
  icon: MicIcon
}, {
  id: '4',
  type: 'article',
  title: 'Brand Architecture as Organizational Strategy: The Multi-Division Challenge',
  excerpt: 'When a company operates across multiple divisions with distinct market identities, brand architecture becomes a proxy for organizational strategy. The choices made in the brand system reflect — and reinforce — how the company thinks about itself.',
  tag: 'Brand Strategy',
  icon: BookOpenIcon
}];
const typeLabels = {
  article: 'Article',
  talk: 'Talk',
  framework: 'Framework'
};
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
export function ThoughtLeadership() {
  const [feedLoading, setFeedLoading] = useState(true);
  const [feedError, setFeedError] = useState(false);
  const feedContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Check if feed content appears after script loads
    const checkFeedContent = () => {
      if (feedContainerRef.current && feedContainerRef.current.querySelector('a')) {
        setFeedLoading(false);
      }
    };
    const script = document.createElement('script');
    script.src = 'https://rss.bloople.net/?url=https%3A%2F%2Fwww.linkedin.com%2Frss%2Ffeed&showtitle=false&type=js';
    script.async = true;
    script.onload = () => {
      // Give the script time to inject content
      setTimeout(() => {
        checkFeedContent();
        setFeedLoading(false);
      }, 1500);
    };
    script.onerror = () => {
      setFeedLoading(false);
      setFeedError(true);
    };
    // Insert script before the feed container so it targets it
    if (feedContainerRef.current) {
      feedContainerRef.current.parentNode?.insertBefore(script, feedContainerRef.current);
    } else {
      document.body.appendChild(script);
    }
    // Fallback timeout
    const timeout = setTimeout(() => {
      setFeedLoading(false);
    }, 4000);
    return () => {
      clearTimeout(timeout);
      script.remove();
    };
  }, []);
  return <section id="thought-leadership" className="py-24 lg:py-32 bg-[#2F3A45]" aria-labelledby="thought-leadership-heading" data-id="element-551">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-552">
        {/* Header */}
        <div className="mb-16" data-id="element-553">
          <span className="inline-flex items-center gap-2 text-[#B08D57] text-xs font-body font-semibold tracking-[0.2em] uppercase mb-3" data-id="element-554">
            <span className="w-6 h-px bg-[#B08D57]" aria-hidden="true" data-id="element-555" />
            Authority & Insight
          </span>
          <h2 id="thought-leadership-heading" className="font-heading font-bold text-[#F7F7F5] text-3xl lg:text-4xl mb-4" data-id="element-556">
            Perspectives on Enterprise Design Leadership
          </h2>
          <p className="font-body text-[#9AABB6] max-w-2xl" data-id="element-557">
            Curated thinking on design systems, federal modernization, AI
            governance, and the organizational dynamics that determine whether
            enterprise design programs succeed or fail.
          </p>
        </div>

        {/* Featured insights */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} role="list" aria-label="Featured insights" data-id="element-558">
          {insights.map(insight => {
          const Icon = insight.icon;
          return <motion.div key={insight.id} variants={itemVariants} role="listitem" data-id="element-559">
                <Card bordered className="h-full bg-[#F7F7F5]/5 border-[#F7F7F5]/10" data-id="element-560">
                  <CardBody className="flex flex-col gap-4" data-id="element-561">
                    <div className="flex items-start justify-between gap-3" data-id="element-562">
                      <div className="w-9 h-9 rounded-lg bg-[#B08D57]/20 flex items-center justify-center flex-shrink-0" aria-hidden="true" data-id="element-563">
                        <Icon className="w-4 h-4 text-[#B08D57]" data-id="element-564" />
                      </div>
                      <div className="flex gap-2" data-id="element-565">
                        <Badge variant="gold" data-id="element-566">{insight.tag}</Badge>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-body text-[#C4D4DC] border border-[#F7F7F5]/20" data-id="element-567">
                          {typeLabels[insight.type]}
                        </span>
                      </div>
                    </div>

                    <h3 className="font-heading font-semibold text-[#F7F7F5] text-base leading-snug" data-id="element-568">
                      {insight.title}
                    </h3>

                    <p className="font-body font-light text-[#ADBEC8] text-sm leading-relaxed flex-1" data-id="element-569">
                      {insight.excerpt}
                    </p>
                  </CardBody>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* LinkedIn Live Feed section */}
        <motion.div className="border border-[#F7F7F5]/10 rounded-2xl overflow-hidden" initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-570">
          {/* Feed header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-8 py-6 border-b border-[#F7F7F5]/10" data-id="element-571">
            <div className="flex items-center gap-3" data-id="element-572">
              <div className="w-10 h-10 rounded-lg bg-[#0077B5] flex items-center justify-center flex-shrink-0" aria-hidden="true" data-id="element-573">
                <LinkedinIcon className="w-5 h-5 text-white" data-id="element-574" />
              </div>
              <div data-id="element-575">
                <h3 className="font-heading font-semibold text-[#F7F7F5] text-lg flex items-center gap-2" data-id="element-576">
                  LinkedIn Activity
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#B08D57]/20 text-[#B08D57] text-xs font-body font-semibold" data-id="element-577">
                    <RssIcon className="w-3 h-3" aria-hidden="true" data-id="element-578" />
                    Live Feed
                  </span>
                </h3>
                <p className="font-body text-[#9AABB6] text-sm" data-id="element-579">
                  @michaelkbigosjr
                </p>
              </div>
            </div>
            <Button variant="primary" size="sm" as="a" href="https://www.linkedin.com/in/michaelkbigosjr/" rightIcon={<ExternalLinkIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-581" />} aria-label="Connect with Michael K. Bigos on LinkedIn (opens in new tab)" data-id="element-580">
              Connect on LinkedIn
            </Button>
          </div>

          {/* Feed content area */}
          <div className="p-8" aria-live="polite" aria-label="LinkedIn feed content" data-id="element-582">
            {feedLoading && <div className="flex items-center justify-center gap-3 py-8 text-[#9AABB6]" data-id="element-583">
                <Loader2Icon className="w-5 h-5 animate-spin text-[#B08D57]" aria-hidden="true" data-id="element-584" />
                <span className="font-body text-sm" data-id="element-585">
                  Loading LinkedIn feed…
                </span>
              </div>}

            {feedError && !feedLoading && <div className="text-center py-8" data-id="element-586">
                <p className="font-body text-[#9AABB6] text-sm mb-3" data-id="element-587">
                  Live feed unavailable — LinkedIn restricts direct RSS access.
                </p>
                <Button variant="ghost" size="sm" as="a" href="https://www.linkedin.com/in/michaelkbigosjr/" rightIcon={<ExternalLinkIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-589" />} aria-label="View Michael K. Bigos LinkedIn profile directly" data-id="element-588">
                  View Profile Directly
                </Button>
              </div>}

            {/* RSS feed target — bloople script injects content here */}
            <div ref={feedContainerRef} id="linkedin-rss-feed" className="linkedin-feed-container" aria-label="LinkedIn posts feed" data-id="element-590" />
          </div>
        </motion.div>
      </div>
    </section>;
}